// 底部操作列 — 橫版依 Unity prefab 原值排版（BottomBar.prefab → BottomBarView.prefab → LandscapeUI 覆寫，
// 三層 Prefab Variant 鏈已逐節點解析；1920×1080 設計座標）：
//
//   Bg              pic_under_land 滿版寬、釘畫面底緣、高 162（Image Type=Simple 橫向拉伸）
//   Buttons 群      錨點＝畫面底緣中央上方 85，以下 x 皆相對畫面中央
//     Menu   x −880  btn_set 84×84            Info   x −750  btn_info 84×84 ×0.85
//     Wallet x −495  pic_numBg 380×54 ＋ btn_wallet 68×68 @x −655 ＋ ₱/餘額（36，左對齊起於 x −625）
//     Bet    x  320  btn_bet_ 84×84 ＋ pic_numBg 230×54 @x 427 ＋ ₱/押注（36，置中於 x 435）
//     Turbo  x  590  btn_fast_normal／btn_fast_quick 84×84
//     Auto   x  680  btn_auto／btn_auto_on 84×84
//     Spin   x  828  底緣上方 140、scale 0.71 → btn_spin_up 308²→218.68；自動剩餘次數字 150→106.5；infinitas
//   Win 群          中央、底緣上方 80：pic_marquee_1 700×144 疊 pic_marquee_2 695.5×145 ＋「贏分」字圖＋₱金額
//   Marquee 群      中央、底緣上方 80：pic_marquee_1 650×144 ＋ 遮罩 550×100 內的捲動橫幅（見 MarqueeView）
//
// Win 與 Marquee 互斥（對應 Unity BottomBarUIMediator 的 ShowMarquee(!isTargetWin)）。
// ⚠️ 本列的字**沒有一個是純白**：餘額/押注各有自己的 m_fontColor、贏分金額走 TMP_ColorGradient preset、
//    自動剩餘次數是精靈字（tx_marquee）。逐項出處見下方色彩常數與 winValueStyle()。
// ⚠️ 這些節點在 Unity 錨在「畫面底緣」（anchorMin/Max.y=0）而非設計中心，Expand 縮放下實際底緣＝視窗底緣，
//    故一律用區域函式 at()（畫面中央 + ux·s，視窗底緣 − uy·s）換算，不可套 unityAnchorLandscape（中心錨定版）。
// ⚠️ Unity 底部列**沒有押注 ± 鈕**（btn_minus/btn_plus 只出現在 BuyBonus.prefab），押注一律點鈕開押注網格。
// [已知簡化] Unity SPIN 鈕另疊 ani_spin 骨骼（426×420），本移植未接（骨骼未複製）。
// [直版] 直版**不是** PoriraitUI 對 BottomBar 的覆寫，而是另一個 prefab：`BottomBar_Portrait.prefab`
//    （BottomBar.prefab 的 Prefab Variant，PoriraitUI 掛的是它；LandscapeUI 掛的才是 BottomBar.prefab）。
//    其覆寫值已逐節點解析（1080×1920 設計座標，見 layoutPortrait），排列順序與橫版完全不同（見 *_P 常數區）。
//    ⚠️ 解析要訣：m_Modifications[].target.fileID 是「直接來源 prefab 資產」id 空間中的 id；BottomBarView 這種
//    更深一層的巢狀節點，其在該空間的 id ＝「深層 fileID XOR 該 PrefabInstance 的 anchor id」（composite）。

import {
  Circle,
  Container,
  FillGradient,
  NineSliceSprite,
  Sprite,
  Text,
  type DestroyOptions,
  type TextStyleOptions,
  type Texture,
} from 'pixi.js'
import {
  unityAnchorPortrait,
  unityScaleLandscape,
  unityScalePortrait,
  type Orientation,
  type Resizable,
} from '../../framework/render/design'
import { formatMoney } from '../../framework/core/format'
import { getCurrencyColor, getCurrencySymbol } from '../../framework/core/currency'
import { logger } from '../../framework/core/Logger'
import { fontFamilyForLocale, t } from '../../framework/i18n/i18n'
import type { EventBus } from '../../framework/events/EventBus'
import type { GameEventMap } from '../events/GameEvents'
import { BOTTOM_WIN_TWEEN_S } from '../constants'
import { getUiTexture } from '../assets/UiTextures'
import { BitmapNumber } from './BitmapNumber'
import { IconButton } from './hud/IconButton'
import { MarqueeView } from './hud/MarqueeView'
import { isAnyModalOpen } from './hud/modalGate'
import { killTweensOf, tweenTo } from './tween'

// 幣別符號改由 getCurrencySymbol() 依後端幣別代碼取得（開機 main.ts setCurrentCurrency 定案）；
// 餘額群另以 getCurrencyColor() 套當前幣別色（見下方 BALANCE_COLOR 說明）。

// ── 字色（皆取自 Unity TMP 序列化值，先前一律誤用純白） ──
/**
 * 押注 ₱ 符號的固定色：`m_fontColor` #FBF2B4 淡金。Unity `OnChangeBalance` 只對「餘額」呼叫
 * `SetCurrencyUI(setColor=true)`（符號＋金額套 cur.Color），win／bet 僅 `GetCurrencyText` 換符號、
 * **不改色**，故押注符號保留此 prefab 色；餘額群改用 `getCurrencyColor()`（COMBO 平台 setColor=true）。
 */
const BALANCE_COLOR = 0xfbf2b4
/** 押注數值（Bet/Text/ValueText）：`m_fontColor` #F9F8B8 淡黃。 */
const BET_COLOR = 0xf9f8b8
/** 贏分金額漸層（TMP_ColorGradient preset `Color_BottomBar_Currency`）。 */
const WIN_GRADIENT_TOP = 0xfafafa
const WIN_GRADIENT_BOTTOM = 0xffaf00
/** 贏分金額描邊色（材質 preset `_OutlineColor` #640000 暗紅）與相對字級的描邊／陰影厚度。 */
const WIN_OUTLINE_COLOR = 0x640000
const WIN_OUTLINE_RATIO = 0.09
const WIN_SHADOW_RATIO = 0.045

// ── Unity BottomBar 橫版版面常數（設計 px；x 相對畫面中央、y 為「距畫面底緣」） ──
/** Bg 高度（pic_under_land sizeDelta.y）。 */
const BAR_H = 162
/** Buttons 群錨點高度（aPos.y=85），以下按鈕皆落在此高度。 */
const ROW_Y = 85
/** 一般圖示邊長（btn_* 皆 84×84）。 */
const ICON = 84
const MENU_X = -880
const INFO_X = -750
/** Info 節點 localScale 0.85（其餘按鈕為 1）。 */
const INFO_SCALE = 0.85
const WALLET_PLATE_X = -495
const WALLET_PLATE_W = 380
const WALLET_BTN_X = -655
const WALLET_BTN_SIZE = 68
/** 餘額文字群左緣（Unity Wallet/Text pivot x=0、rect 左緣 x=335）；HLG MiddleLeft。 */
const WALLET_TEXT_LEFT = -625
const BET_BTN_X = 320
const BET_PLATE_X = 427
const BET_PLATE_W = 230
/** 押注文字群中心（Unity Bet/Text aPos 115 相對 Bet 的 320）；HLG MiddleCenter。 */
const BET_TEXT_X = 435
/** pic_numBg 共用高度與 9-slice 左右邊界（貼圖 186×54、spriteBorder x/z=31，只橫向延展）。 */
const PLATE_H = 54
const PLATE_SLICE = 31
/** 餘額／押注文字級（Unity TMP fontSize 36）與群內間距（HLG spacing 5）。 */
const NUM_FONT = 36
const NUM_GAP = 5
const TURBO_X = 590
const AUTO_X = 680
/** SPIN 群：aPos (828, 55) 相對 Buttons 的 85 → 距底緣 140；localScale 0.71。 */
const SPIN_X = 828
const SPIN_Y = 140
const SPIN_SCALE = 0.71
/** btn_spin_up 原生邊長（未套 localScale）。 */
const SPIN_BASE = 308
/** SPIN 鈕實際顯示邊長（橫版 308² × 0.71）。 */
const SPIN_SIZE = SPIN_BASE * SPIN_SCALE
/** 自動遊戲剩餘次數字（TMP fontSize 150 × 0.71）與其 y 偏移（aPos.y −10 × 0.71，Unity y 向上）。 */
const SPIN_NUM_FONT = 150 * SPIN_SCALE
const SPIN_NUM_DY = 10 * SPIN_SCALE
/** 無限次符號 infinitas 138×178 × 0.71。 */
const INFINITAS_W = 138 * SPIN_SCALE
const INFINITAS_H = 178 * SPIN_SCALE
/** Win／Marquee 群錨點高度（皆 aPos.y=80），底板再往上 9。 */
const CENTER_Y = 80
const CENTER_BG_DY = 9
const WIN_PLATE_W = 700
const WIN_PLATE_H = 144
/** 疊在 Win 底板上的粉紅漸層（Win/Bg/Image 的 pic_marquee_2，sizeDelta 695.5×145、與底板同心）。 */
const WIN_PLATE2_W = 695.5
const WIN_PLATE2_H = 145
/** 贏分內容群 y 偏移（Win/Layout aPos.y=8）。 */
const WIN_LAYOUT_DY = 8
/** Win/Layout 及其子節點的 localScale 1.15（「贏分」字圖高 70、金額 TMP fontSize 49 皆再乘上它）。 */
const WIN_CONTENT_SCALE = 1.15
const WIN_LABEL_H = 70 * WIN_CONTENT_SCALE
const WIN_FONT = 49 * WIN_CONTENT_SCALE
const WIN_GAP = NUM_GAP * WIN_CONTENT_SCALE
const MARQUEE_PLATE_W = 650
const MARQUEE_MASK_W = 550
const MARQUEE_MASK_H = 100

/**
 * 免費遊戲 SPIN 鈕疊加群組版面（Unity BottomBar 的 Game/FreeGame 群組，aPos (828, 140) 底部錨定；
 * 內含 BgImage 金繩框大圓、ValueText 剩餘次數（tx_freeSpin 金色位圖字）、TextImage「免費旋轉」字圖）。
 * 尺寸為對照 Unity 實機截圖的顯示值（prefab sizeDelta 另有整體縮放，故以實機為準）。
 */
const FG_DISC_SIZE = 234
const FG_NUMBER_H = 95
const FG_NUMBER_DY = -8
const FG_LABEL_W = 165
const FG_LABEL_DY = 55

// ── 直版（Unity BottomBar_Portrait.prefab，BottomBar.prefab 的 Variant；1080×1920 設計座標）─────
// 排列與橫版完全不同（不可沿用橫版順序）：同一水平線由左至右 Menu／Bet／Auto／Turbo／Info，
// SPIN 在正中央、比那排高 43，Wallet 餘額牌在正中央最下層。
// x 相對畫面中央；uy＝距**畫面底緣**的設計 px（Buttons 群 anchor(0.5,0) aPos.y=70，各鈕再加自身 aPos.y）。
/** Bg（pic_under_por）sizeDelta.y。 */
const BAR_H_P = 308
/** Buttons 群錨點高度（aPos.y=70）＋各鈕 aPos.y=152 → 同一排的高度。 */
const ROW_Y_P = 70 + 152
const MENU_X_P = -476
const BET_X_P = -260
const AUTO_X_P = 230
const TURBO_X_P = 360
const INFO_X_P = 469
/** Bet／Auto 的節點 sizeDelta 在直版放大為 156×156（其餘仍 84）。 */
const ICON_BIG_P = 156
/** Bet/NumBg 與 Bet/Text 的 aPos (0,−74)（相對 Bet 節點）、NumBg sizeDelta.x=187。 */
const BET_PLATE_DY_P = -74
const BET_PLATE_W_P = 187
/** Wallet：aPos (0,4)；NumBg 400×54 置中、CurrencyButton x −185、Text 左緣 x −150。 */
const WALLET_Y_P = 70 + 4
const WALLET_PLATE_W_P = 400
const WALLET_BTN_X_P = -185
const WALLET_TEXT_LEFT_P = -150
/**
 * SPIN 群 aPos.y=195（相對 Buttons 的 70）→ 距底緣 265；FreeGame 群同為底部錨定 aPos (0, 265)。
 * 兩者 localScale 都由橫版的 0.71 覆寫為 1，故直版的等效倍率一律是 s / SPIN_SCALE。
 */
const SPIN_Y_P = 70 + 195
/** Win 與 Marquee 在直版都改成**畫面中央錨定**、且同為 aPos (0, −330)（橫版是底緣上方 80）。 */
const CENTER_AY_P = -330
/** Marquee 直版覆寫：Bg sizeDelta.x 650→700、NormalMask sizeDelta.x 550→600（高度與 localScale 皆沿用橫版）。 */
const MARQUEE_PLATE_W_P = 700
const MARQUEE_MASK_W_P = 600

/** 跑馬燈四張橫幅字圖（Unity 正宗 tx_score_1~4，per-locale）；任一張未載入即整組回空＝文字模式。 */
function marqueeBanners(): Texture[] {
  try {
    return [getUiTexture('txScore1'), getUiTexture('txScore2'), getUiTexture('txScore3'), getUiTexture('txScore4')]
  } catch {
    return []
  }
}

/** pic_numBg 數值底板（186×54，僅左右 9-slice；NineSliceSprite 無 anchor，故一律由左上角定位）。 */
function numPlate(): NineSliceSprite {
  return new NineSliceSprite({
    texture: getUiTexture('numBg'),
    leftWidth: PLATE_SLICE,
    rightWidth: PLATE_SLICE,
    topHeight: 0,
    bottomHeight: 0,
  })
}

/**
 * 底部列數值文字（餘額／押注，Unity TMP fontSize 36）；anchorX 0=左錨、1=右錨。
 * 字色取自各 TMP 的 `m_fontColor`（見 BALANCE_COLOR／BET_COLOR），非白色。
 */
function numText(anchorX: number, color: number): Text {
  const text = new Text({
    text: '',
    style: { fontFamily: fontFamilyForLocale(), fontSize: NUM_FONT, fill: color },
  })
  text.anchor.set(anchorX, 0.5)
  return text
}

/**
 * 贏分金額（Win/Layout 的 CurrencyText／ValueText）樣式 — Unity 兩者共用同一組 TMP 設定：
 *   面色＝TMP_ColorGradient preset `Color_BottomBar_Currency`（上 #FAFAFA → 下 #FFAF00 垂直漸層），
 *   描邊＝材質 preset 的 `_OutlineColor` #640000，另有 `UNDERLAY_ON` 黑色正下方陰影。
 * 描邊／陰影厚度隨字級等比（TMP 的 _OutlineWidth 是相對字身的 SDF 比例，非固定 px）。
 */
function winValueStyle(fontSize: number): TextStyleOptions {
  return {
    fontFamily: fontFamilyForLocale(),
    fontSize,
    fill: new FillGradient({
      type: 'linear',
      start: { x: 0, y: 0 },
      end: { x: 0, y: 1 },
      colorStops: [
        { offset: 0, color: WIN_GRADIENT_TOP },
        { offset: 1, color: WIN_GRADIENT_BOTTOM },
      ],
      textureSpace: 'local',
    }),
    stroke: { color: WIN_OUTLINE_COLOR, width: Math.max(1, fontSize * WIN_OUTLINE_RATIO) },
    dropShadow: {
      color: 0x000000,
      alpha: 1,
      angle: Math.PI / 2,
      blur: 0,
      distance: Math.max(1, fontSize * WIN_SHADOW_RATIO),
    },
  }
}

/** 已換算完成的螢幕座標點。 */
type BarPoint = { readonly x: number; readonly y: number }

/** Unity 底部錨定座標 → 螢幕座標的轉換函式。 */
type BarAnchor = (ux: number, uy: number) => BarPoint

export class BottomBarView extends Container implements Resizable {
  private readonly background = new Sprite(getUiTexture('barBg'))

  // ── 左群：選單／資訊／錢包餘額 ──
  private readonly menuBtn = new IconButton(getUiTexture('iconMenu'), () => this.bus.emit('menuClicked', undefined))
  private readonly infoBtn = new IconButton(getUiTexture('iconInfo'), () => this.bus.emit('infoClicked', undefined))
  private readonly walletBtn = new IconButton(getUiTexture('iconWallet'), () => logger.info('點擊錢包 — UI 佔位'))
  private readonly walletPlate = numPlate()
  private readonly balanceCurrency = numText(0, getCurrencyColor())
  private readonly balanceText = numText(0, getCurrencyColor())

  // ── 中央：贏分帶（Win）與跑馬燈（Marquee）互斥共用同一槽位 ──
  private readonly winGroup = new Container()
  private readonly winPlate = new Sprite(getUiTexture('marqueeBg'))
  private readonly winPlateTop = new Sprite(getUiTexture('winBandBg'))
  /** 「贏分」標題字圖（Unity Win/Layout/TextImage 的 tx_win，per-locale）。 */
  private readonly winLabel = new Sprite(getUiTexture('txWin'))
  private readonly winCurrency = new Text({ text: getCurrencySymbol(), style: winValueStyle(WIN_FONT) })
  private readonly winText = new Text({ text: '', style: winValueStyle(WIN_FONT) })
  private readonly marqueeGroup = new Container()
  private readonly marqueePlate = new Sprite(getUiTexture('marqueeBg'))
  // 閒置時輪播 Unity 正宗跑馬燈字圖 tx_score_1~4（S026_AssetTable per-locale）；
  // 字圖未載入時退回多語系宣傳文字（game.Loading_1~3）。
  private readonly marquee = new MarqueeView(
    [t('game.Loading_1'), t('game.Loading_2'), t('game.Loading_3')],
    marqueeBanners(),
  )

  // ── 右群：押注（點鈕／數值牌開押注網格，Unity 無 ± 鈕） ──
  private readonly betBtn = new IconButton(getUiTexture('iconBet'), () => this.openBetMenu())
  private readonly betPlate = numPlate()
  private readonly betCurrency = numText(1, BALANCE_COLOR)
  private readonly betText = numText(0, BET_COLOR)

  // ── 右群：Turbo / Auto / Spin ──
  private turboOn = false
  private readonly turboBtn = new IconButton(getUiTexture('iconTurbo'), () => this.toggleTurbo())
  private readonly autoBtn = new IconButton(getUiTexture('iconAuto'), () => this.handleAutoClick())
  private readonly spinButton = new Sprite(getUiTexture('spinButton'))
  /**
   * 自動遊戲進行中，SPIN 鈕上顯示剩餘次數（Unity AutoSpinsValueText）。
   * 該 TMP 同樣是**精靈字**（`m_fontAsset` 為空字型、字面來自 `m_spriteAsset: tx_marquee`），
   * 故用 tx_marquee 位圖字還原，而非白字＋描邊。
   */
  private readonly autoCountText = new BitmapNumber('marquee')
  /** 自動遊戲「無限次」符號（Unity InfinitasImage）。 */
  private readonly infinitas = new Sprite(getUiTexture('infinitas'))

  // ── 免費遊戲 SPIN 鈕疊加群組（對照 Unity Game/FreeGame，FG 期間取代一般 SPIN 鈕） ──
  private readonly fgSpinDisc = new Sprite(getUiTexture('freeSpinDisc'))
  /** FG 剩餘次數（tx_freeSpin 金色位圖字，對應 Unity ValueText）。 */
  private readonly fgSpinNumber = new BitmapNumber('freeSpin')
  /** 「免費旋轉」本地化字圖（對應 Unity TextImage 的 tx_freeSpins）。 */
  private readonly fgSpinLabel = new Sprite(getUiTexture('freeSpinsLabel'))

  private isSpinning = false
  // SPIN 鈕中央顯示狀態：免費遊戲剩餘（fg*）與自動遊戲剩餘（auto*），由 refreshSpinCenter 統一決定顯示。
  private fgActive = false
  private fgRemaining = 0
  private autoActive = false
  private autoRemaining = 0
  private autoInfinite = false
  /** 贏分數字滾動動畫的當前顯示值（tween 對象）；與 winText 顯示的金額同步，用來判斷該滾動還是瞬間歸零。 */
  private readonly winProxy = { value: 0 }

  // ── 由 resize 計算、供金額變動時就地重排文字的當前度量 ──
  private numGap = NUM_GAP
  /** 設定鈕當前顯示邊長（供 menuButtonAnchor 定位選單浮層）。 */
  private menuIconSize = ICON
  /** 當前「Unity 設計 px → 螢幕 px」縮放（橫直版各取自己那套 Canvas；供 betButtonAnchor 給浮層沿用）。 */
  private designScale = 1
  private betTextCenterX = 0
  private winContentCenterX = 0
  private winContentY = 0
  private winContentGap = WIN_GAP

  /** 空白鍵監聽（destroy 時解除）。 */
  private readonly keydownHandler: (event: KeyboardEvent) => void

  constructor(private readonly bus: EventBus<GameEventMap>) {
    super()
    for (const sprite of [
      this.winPlate,
      this.winPlateTop,
      this.marqueePlate,
      this.winLabel,
      this.spinButton,
      this.infinitas,
      this.fgSpinDisc,
      this.fgSpinLabel,
    ]) {
      sprite.anchor.set(0.5)
    }
    this.winCurrency.anchor.set(0, 0.5)
    this.winText.anchor.set(0, 0.5)

    // Unity Win/Bg → Win/Bg/Image：粉紅漸層 695.5×145 疊在深棕底板 700×144 上（同心、皆 Simple 拉伸）。
    // 兩張以同層兄弟節點疊放（PixiJS v8 不允許 Sprite 收子節點）。
    this.winGroup.addChild(this.winPlate, this.winPlateTop, this.winLabel, this.winCurrency, this.winText)
    this.winGroup.visible = false
    this.marqueeGroup.addChild(this.marqueePlate, this.marquee)

    this.addChild(
      this.background,
      this.winGroup,
      this.marqueeGroup,
      this.menuBtn,
      this.infoBtn,
      this.walletPlate,
      this.walletBtn,
      this.balanceCurrency,
      this.balanceText,
      this.betPlate,
      this.betBtn,
      this.betCurrency,
      this.betText,
      this.turboBtn,
      this.autoBtn,
      this.spinButton,
      this.autoCountText,
      this.infinitas,
      this.fgSpinDisc,
      this.fgSpinNumber,
      this.fgSpinLabel,
    )

    this.spinButton.eventMode = 'static'
    this.spinButton.cursor = 'pointer'
    // spin 進行中按下＝快停（對應 Unity OnSpin：Rotate/StopSpin/FillSymbol 時 ForceStop）；閒置＝開轉。
    this.spinButton.on('pointertap', () => this.handleSpinPressed())
    // 空白鍵＝SPIN（自動遊戲中按空白＝取消自動；對應 Unity BottomBarUIMediator 的 spaceKey 分支）。
    // 任一彈窗開啟時抑制（Unity 彈窗全螢幕 Mask 擋 raycast 的等價行為；空白鍵不經 PixiJS 事件樹須另行閘控）。
    this.keydownHandler = (event: KeyboardEvent): void => {
      if (event.code !== 'Space' || event.repeat) return
      event.preventDefault()
      if (isAnyModalOpen()) return
      // 自動遊戲中按空白＝停止自動：交由 handleSpinPressed 統一判斷 autoActive，與 SPIN 鈕行為一致。
      this.handleSpinPressed()
    }
    window.addEventListener('keydown', this.keydownHandler)

    // 金額牌整塊可點 → 同押注鈕開押注網格（Unity 只有 BetButton 可點、數值牌是純底板，
    // 這裡讓底板也可點是無害的便利性延伸）；疊在上層的文字不攔截點擊。
    this.betPlate.eventMode = 'static'
    this.betPlate.cursor = 'pointer'
    for (const overlay of [this.betCurrency, this.betText]) overlay.eventMode = 'none'
    this.betPlate.on('pointertap', () => this.openBetMenu())

    this.bus.on('balanceChanged', ({ balance }) => this.setBalance(balance))
    this.bus.on('betChanged', ({ bet }) => this.setBet(bet))
    this.bus.on('winShown', ({ amount }) => this.setWin(amount))
    this.bus.on('spinStateChanged', ({ spinning }) => this.setSpinning(spinning))

    // 免費遊戲：SPIN 鈕換成 FreeGame 疊加大圓（金繩框＋剩餘次數＋「免費旋轉」字圖）；
    // 押注／自動／資訊鈕禁用（對應 Unity OnSetBottomUiNormal(isFreeGame=true) 的 interactable=false）。
    this.bus.on('freeGameStarted', ({ spinCount }) => {
      this.fgActive = true
      this.fgRemaining = spinCount
      this.autoBtn.setEnabled(false)
      this.infoBtn.setEnabled(false)
      this.refreshSpinCenter()
    })
    // 剩餘次數＝總數−已開轉數（開轉時遞減；retrigger 加次數時同步增加）。對應 Unity UpdateFreeGameCount。
    this.bus.on('freeGameProgress', ({ current, total }) => {
      this.fgRemaining = Math.max(0, total - current)
      this.refreshSpinCenter()
    })
    this.bus.on('freeGameEnded', () => {
      this.fgActive = false
      this.autoBtn.setEnabled(true)
      this.infoBtn.setEnabled(true)
      this.refreshSpinCenter()
    })
    // 自動遊戲狀態（開始/剩餘遞減/停止，含餘額不足時 GameController 自動關閉）：同步自動鈕圖與 SPIN 鈕剩餘次數。
    this.bus.on('autoSpinStateChanged', ({ active, remaining, infinite }) => {
      this.autoActive = active
      this.autoRemaining = remaining
      this.autoInfinite = infinite
      this.autoBtn.setTexture(getUiTexture(active ? 'iconAutoOn' : 'iconAuto'))
      this.refreshSpinCenter()
    })

    this.setBalance(0)
    this.setBet(0)
    this.setWin(0)
    this.refreshSpinCenter()
  }

  private setBalance(balance: number): void {
    this.balanceCurrency.text = getCurrencySymbol()
    this.balanceText.text = formatMoney(balance)
    this.layoutBalanceText()
  }

  private setBet(bet: number): void {
    // 押注符號（對照 Unity OnChangeBalance 對 betCurrencyText 設 GetCurrencyText；
    // 先前漏設 betCurrency.text 致押注恆無符號，此處補上並套當前幣別）。
    this.betCurrency.text = getCurrencySymbol()
    this.betText.text = formatMoney(bet)
    this.layoutBetText()
  }

  /**
   * 贏分遞增（消除連鎖逐步加總）時滾動播放（0.25s Linear，對應 Unity winValueTweenDuration/Ease.Linear）；
   * 歸零或減少（新一輪開始重置）則立即更新，避免「數字從上一輪贏分倒數回 0」這種不合理的視覺效果。
   */
  private setWin(amount: number): void {
    killTweensOf(this.winProxy)

    if (amount <= this.winProxy.value) {
      this.winProxy.value = amount
      this.renderWin(amount)
      return
    }

    void tweenTo(this.winProxy, {
      value: amount,
      duration: BOTTOM_WIN_TWEEN_S,
      ease: 'none',
      onUpdate: () => this.renderWin(this.winProxy.value),
    })
  }

  /** 有贏分時中央顯示贏分帶並暫停跑馬燈；歸零時還原跑馬燈（對應 Unity ShowMarquee(!isTargetWin)）。 */
  private renderWin(amount: number): void {
    if (amount > 0) {
      this.winText.text = formatMoney(amount)
      this.winGroup.visible = true
      this.marqueeGroup.visible = false
      this.marquee.stop()
      this.layoutWinContent()
    } else {
      this.winGroup.visible = false
      this.marqueeGroup.visible = true
      this.marquee.start()
    }
  }

  /**
   * SPIN 鈕按下（含空白鍵）：
   *   自動遊戲進行中 → 停止自動遊戲（GameController.stopAuto 清除剩餘次數並廣播 autoSpinStateChanged
   *     {active:false} → refreshSpinCenter 復原 SPIN／自動鈕一般造型、隱藏剩餘次數）。此時 SPIN 鈕顯示為
   *     停止鈕造型 btn_spin_stop，對應 Unity auto 時最上層常駐的 AutoSpinStopButton；
   *   一般 spin 進行中 → 快停（Unity spinStopSprite 狀態的行為）；
   *   閒置 → 開轉。
   */
  private handleSpinPressed(): void {
    if (this.autoActive) {
      this.bus.emit('autoSpinStopped', undefined)
      return
    }
    if (this.isSpinning) this.bus.emit('forceStopRequested', undefined)
    else this.bus.emit('spinRequested', undefined)
  }

  /** 押注鈕／數值牌：開押注網格彈窗（僅閒置時，對應 Unity BetListWindow）。 */
  private openBetMenu(): void {
    if (!this.isSpinning) this.bus.emit('betMenuClicked', undefined)
  }

  private setSpinning(spinning: boolean): void {
    this.isSpinning = spinning
    this.betBtn.setEnabled(!spinning)
    // SPIN 底圖（快停鈕 vs 閒置鈕，以及自動遊戲的停止鈕造型）交由 refreshSpinCenter 統一決定。
    this.refreshSpinCenter()
  }

  private toggleTurbo(): void {
    this.turboOn = !this.turboOn
    this.turboBtn.setTexture(getUiTexture(this.turboOn ? 'iconTurboOn' : 'iconTurbo'))
    this.bus.emit('turboToggled', { enabled: this.turboOn })
    // Turbo 開啟狀態下於 spin 進行中按下＝快停（對應 Unity SlotStateMachine.OnUITurboClickEvent）。
    if (this.turboOn && this.isSpinning) this.bus.emit('forceStopRequested', undefined)
  }

  /**
   * 設定鈕（btn_set）中心的螢幕座標與顯示邊長 — 供系統選單浮層對齊到鈕正上方
   * （Unity 的選單就地由設定鈕往上展開，非置中彈窗）。
   */
  menuButtonAnchor(): { x: number; y: number; size: number } {
    return { x: this.menuBtn.x, y: this.menuBtn.y, size: this.menuIconSize }
  }

  /**
   * 押注鈕（btn_bet_，Unity `Bet` 節點）中心的螢幕座標與當前設計→螢幕縮放 — 供押注網格浮層排版。
   * Unity `BottomBarUI.SetBetListWindow` 是把 BetListWindow prefab Instantiate 到這個節點下、
   * 並強制 `anchoredPosition = Vector2.zero`，所以浮層的所有座標都以此點為原點的設計 px。
   */
  betButtonAnchor(): { x: number; y: number; scale: number } {
    return { x: this.betBtn.x, y: this.betBtn.y, scale: this.designScale }
  }

  /** SPIN 鈕中心的螢幕座標（retrigger 加次數子彈的飛行終點，對應 Unity _freeSpinDummy）；FG 期間為大圓中心。 */
  spinButtonCenter(): { x: number; y: number } {
    if (this.fgActive) return { x: this.fgSpinDisc.x, y: this.fgSpinDisc.y }
    return { x: this.spinButton.x, y: this.spinButton.y }
  }

  /** 自動鈕：進行中則停止，否則開啟次數面板（對應 Unity UIAutoClickEvent 與自動進行中的停止鈕）。 */
  private handleAutoClick(): void {
    if (this.autoActive) this.bus.emit('autoSpinStopped', undefined)
    else this.bus.emit('autoSpinClicked', undefined)
  }

  /**
   * 統一決定 SPIN 鈕區顯示：
   *   免費遊戲 → 隱藏一般 SPIN 鈕、顯示 FreeGame 大圓（金繩框＋剩餘次數位圖字＋「免費旋轉」字圖）；
   *   自動遊戲 → SPIN 鈕改用停止鈕造型 btn_spin_stop（對應 Unity auto 時最上層常駐的 AutoSpinStopButton），
   *              並上疊剩餘次數（無限則改疊 infinitas 符號，對應 Unity InfinitasImage）；
   *   其餘 → 只有 SPIN 鈕本體（Unity 的 SPIN 字樣就畫在 btn_spin_up 圖上，不另疊文字）。
   */
  private refreshSpinCenter(): void {
    const fg = this.fgActive
    this.fgSpinDisc.visible = fg
    this.fgSpinNumber.visible = fg
    this.fgSpinLabel.visible = fg
    this.spinButton.visible = !fg
    // SPIN 底圖：自動遊戲進行中一律用停止鈕造型 btn_spin_stop（對應 Unity auto 時最上層常駐的
    // AutoSpinStopButton，蓋住底層在 idle/lock 間切換的 spinBtn，故玩家全程看到停止鈕造型、不會在
    // 續轉空檔露出 btn_spin_up 的旋轉箭頭）；非自動時 spin 中＝快停鈕、閒置＝btn_spin_up。
    this.spinButton.texture = getUiTexture(this.autoActive || this.isSpinning ? 'spinStop' : 'spinButton')
    const showAuto = !fg && this.autoActive
    this.autoCountText.visible = showAuto && !this.autoInfinite
    this.infinitas.visible = showAuto && this.autoInfinite
    if (fg) {
      this.fgSpinNumber.setText(`${this.fgRemaining}`)
      return
    }
    if (this.autoCountText.visible) this.autoCountText.setText(`${this.autoRemaining}`)
  }

  resize(width: number, height: number, orientation: Orientation): void {
    if (orientation === 'landscape') this.layoutLandscape(width, height)
    else this.layoutPortrait(width, height)
  }

  /** 橫版：Unity BottomBar prefab 原值排版（x 相對畫面中央、y 相對視窗底緣，等比 s 縮放）。 */
  private layoutLandscape(width: number, height: number): void {
    const s = unityScaleLandscape(width, height)
    const at: BarAnchor = (ux, uy) => ({ x: width / 2 + ux * s, y: height - uy * s })
    this.numGap = NUM_GAP * s
    this.designScale = s

    this.background.texture = getUiTexture('barBg')
    this.background.position.set(0, height - BAR_H * s)
    this.background.setSize(width, BAR_H * s)

    // 左群：選單／資訊／錢包
    this.menuIconSize = ICON * s
    this.menuBtn.setIconSize(this.menuIconSize)
    place(this.menuBtn, at(MENU_X, ROW_Y))
    this.infoBtn.setIconSize(ICON * INFO_SCALE * s)
    place(this.infoBtn, at(INFO_X, ROW_Y))
    placePlate(this.walletPlate, at(WALLET_PLATE_X, ROW_Y), WALLET_PLATE_W * s, PLATE_H * s)
    this.walletBtn.setIconSize(WALLET_BTN_SIZE * s)
    place(this.walletBtn, at(WALLET_BTN_X, ROW_Y))
    const balanceAt = at(WALLET_TEXT_LEFT, ROW_Y)
    this.balanceCurrency.style.fontSize = NUM_FONT * s
    this.balanceText.style.fontSize = NUM_FONT * s
    this.balanceCurrency.position.set(balanceAt.x, balanceAt.y)
    this.balanceText.y = balanceAt.y
    this.layoutBalanceText()

    // 中央：贏分帶／跑馬燈（同槽位互斥，橫版兩群同錨點）
    const centerAt = at(0, CENTER_Y + CENTER_BG_DY)
    this.layoutWinBand(centerAt, at(0, CENTER_Y + WIN_LAYOUT_DY), s)
    this.layoutMarqueeBand(centerAt, s, MARQUEE_PLATE_W, MARQUEE_MASK_W)

    // 右群：押注
    placePlate(this.betPlate, at(BET_PLATE_X, ROW_Y), BET_PLATE_W * s, PLATE_H * s)
    this.betBtn.setIconSize(ICON * s)
    place(this.betBtn, at(BET_BTN_X, ROW_Y))
    const betAt = at(BET_TEXT_X, ROW_Y)
    this.betCurrency.style.fontSize = NUM_FONT * s
    this.betText.style.fontSize = NUM_FONT * s
    this.betCurrency.y = betAt.y
    this.betText.y = betAt.y
    this.betTextCenterX = betAt.x
    this.layoutBetText()

    // 右群：Turbo／自動／SPIN
    this.turboBtn.setIconSize(ICON * s)
    place(this.turboBtn, at(TURBO_X, ROW_Y))
    this.autoBtn.setIconSize(ICON * s)
    place(this.autoBtn, at(AUTO_X, ROW_Y))
    this.layoutSpin(at(SPIN_X, SPIN_Y), SPIN_SIZE * s, s)
    this.layoutFreeGameDisc(at(SPIN_X, SPIN_Y), s)
  }

  /**
   * 贏分帶（Win）：兩張同心的 pic_marquee 底板 ＋ 內容群（「贏分」字圖＋₱＋金額）。
   * 底板與內容群的錨點分開傳入，因為兩者在 Unity 是 Win 群下的兩個節點（aPos.y 各為 +9／+8）。
   */
  private layoutWinBand(plateAt: BarPoint, contentAt: BarPoint, s: number): void {
    this.winPlate.position.set(plateAt.x, plateAt.y)
    this.winPlate.setSize(WIN_PLATE_W * s, WIN_PLATE_H * s)
    this.winPlateTop.position.set(plateAt.x, plateAt.y)
    this.winPlateTop.setSize(WIN_PLATE2_W * s, WIN_PLATE2_H * s)

    this.winContentCenterX = contentAt.x
    this.winContentY = contentAt.y
    this.winContentGap = WIN_GAP * s
    this.winLabel.scale.set((WIN_LABEL_H * s) / this.winLabel.texture.height)
    this.setWinFontSize(WIN_FONT * s)
    this.layoutWinContent()
  }

  /**
   * 跑馬燈（Marquee）：與贏分帶同槽位互斥，有自己的 pic_marquee_1 底板與捲動遮罩。
   * 直版底板／遮罩較寬且整群另有 0.8 縮放，故寬度與 s 皆由呼叫端傳入。
   */
  private layoutMarqueeBand(at: BarPoint, s: number, plateW: number, maskW: number): void {
    this.marqueePlate.position.set(at.x, at.y)
    this.marqueePlate.setSize(plateW * s, WIN_PLATE_H * s)
    this.marquee.position.set(at.x, at.y)
    this.marquee.setViewport(maskW * s, MARQUEE_MASK_H * s, NUM_FONT * s)
  }

  /** 套用贏分金額字級（描邊／陰影厚度與漸層皆需隨字級重建，故整組樣式重設）。 */
  private setWinFontSize(fontSize: number): void {
    this.winCurrency.style = winValueStyle(fontSize)
    this.winText.style = winValueStyle(fontSize)
  }

  /** SPIN 鈕本體＋自動剩餘次數／無限符號（皆置中於同一點）。 */
  private layoutSpin(center: { readonly x: number; readonly y: number }, size: number, s: number): void {
    this.spinButton.setSize(size, size)
    this.spinButton.position.set(center.x, center.y)
    this.spinButton.hitArea = new Circle(0, 0, size / 2)
    this.autoCountText.setHeight(Math.max(16, SPIN_NUM_FONT * s))
    this.autoCountText.position.set(center.x, center.y + SPIN_NUM_DY * s)
    this.infinitas.setSize(INFINITAS_W * s, INFINITAS_H * s)
    this.infinitas.position.set(center.x, center.y)
  }

  /** 免費遊戲 SPIN 大圓（Unity Game/FreeGame，與一般 SPIN 鈕同心、突出於底部列頂）。 */
  private layoutFreeGameDisc(center: { readonly x: number; readonly y: number }, s: number): void {
    this.fgSpinDisc.setSize(FG_DISC_SIZE * s, FG_DISC_SIZE * s)
    this.fgSpinDisc.position.set(center.x, center.y)
    this.fgSpinNumber.setHeight(Math.max(24, FG_NUMBER_H * s))
    this.fgSpinNumber.position.set(center.x, center.y + FG_NUMBER_DY * s)
    this.fgSpinLabel.scale.set((FG_LABEL_W * s) / this.fgSpinLabel.texture.width)
    this.fgSpinLabel.position.set(center.x, center.y + FG_LABEL_DY * s)
  }

  /** 餘額文字：₱ 與數字左對齊排列（Unity Wallet/Text 的 HLG MiddleLeft，spacing 5）。 */
  private layoutBalanceText(): void {
    this.balanceText.x = this.balanceCurrency.x + this.balanceCurrency.width + this.numGap
  }

  /** 押注文字：₱ 與數字整組置中（Unity Bet/Text 的 HLG MiddleCenter，spacing 5）。 */
  private layoutBetText(): void {
    const total = this.betCurrency.width + this.numGap + this.betText.width
    // betCurrency 為右錨，故其 x 即「左緣 + 自身寬度」。
    this.betCurrency.x = this.betTextCenterX - total / 2 + this.betCurrency.width
    this.betText.x = this.betCurrency.x + this.numGap
  }

  /** 贏分內容：「贏分」字圖 ＋ ₱ ＋ 金額整組置中（Unity Win/Layout 的 HLG MiddleCenter + ContentSizeFitter）。 */
  private layoutWinContent(): void {
    const gap = this.winContentGap
    const total = this.winLabel.width + gap + this.winCurrency.width + gap + this.winText.width
    let x = this.winContentCenterX - total / 2
    this.winLabel.position.set(x + this.winLabel.width / 2, this.winContentY)
    x += this.winLabel.width + gap
    this.winCurrency.position.set(x, this.winContentY)
    x += this.winCurrency.width + gap
    this.winText.position.set(x, this.winContentY)
  }

  /**
   * 直版：Unity BottomBar_Portrait.prefab 的原值排版（1080×1920 設計座標）。
   * 與橫版同一批節點／素材，差別在排列順序、Bet/Auto 放大為 156²、SPIN 不縮小、餘額牌獨立在正中央最下層，
   * 以及 Win／Marquee 由「底緣錨定」改為「畫面中央錨定」。
   */
  private layoutPortrait(width: number, height: number): void {
    const s = unityScalePortrait(width, height)
    const at: BarAnchor = (ux, uy) => ({ x: width / 2 + ux * s, y: height - uy * s })
    this.numGap = NUM_GAP * s
    this.designScale = s

    this.background.texture = getUiTexture('barBgPortrait')
    this.background.position.set(0, height - BAR_H_P * s)
    this.background.setSize(width, BAR_H_P * s)

    // 同一排（距底緣 222）由左至右：選單／押注／自動／Turbo／資訊
    this.menuIconSize = ICON * s
    this.menuBtn.setIconSize(this.menuIconSize)
    place(this.menuBtn, at(MENU_X_P, ROW_Y_P))
    // 直版 Info 不像橫版縮 0.85。
    this.infoBtn.setIconSize(ICON * s)
    place(this.infoBtn, at(INFO_X_P, ROW_Y_P))
    this.turboBtn.setIconSize(ICON * s)
    place(this.turboBtn, at(TURBO_X_P, ROW_Y_P))
    this.autoBtn.setIconSize(ICON_BIG_P * s)
    place(this.autoBtn, at(AUTO_X_P, ROW_Y_P))

    // 押注：鈕在同一排、數值牌掛在鈕正下方 74
    this.betBtn.setIconSize(ICON_BIG_P * s)
    place(this.betBtn, at(BET_X_P, ROW_Y_P))
    const betAt = at(BET_X_P, ROW_Y_P + BET_PLATE_DY_P)
    placePlate(this.betPlate, betAt, BET_PLATE_W_P * s, PLATE_H * s)
    this.betCurrency.style.fontSize = NUM_FONT * s
    this.betText.style.fontSize = NUM_FONT * s
    this.betCurrency.y = betAt.y
    this.betText.y = betAt.y
    this.betTextCenterX = betAt.x
    this.layoutBetText()

    // 餘額：正中央最下層（400×54 底板＋幣別鈕＋左對齊金額）
    placePlate(this.walletPlate, at(0, WALLET_Y_P), WALLET_PLATE_W_P * s, PLATE_H * s)
    this.walletBtn.setIconSize(WALLET_BTN_SIZE * s)
    place(this.walletBtn, at(WALLET_BTN_X_P, WALLET_Y_P))
    const balanceAt = at(WALLET_TEXT_LEFT_P, WALLET_Y_P)
    this.balanceCurrency.style.fontSize = NUM_FONT * s
    this.balanceText.style.fontSize = NUM_FONT * s
    this.balanceCurrency.position.set(balanceAt.x, balanceAt.y)
    this.balanceText.y = balanceAt.y
    this.layoutBalanceText()

    // 中央贏分帶／跑馬燈：直版兩群都改為畫面中央錨定、同一個 y（盤面下緣與底部列之間），跑馬燈底板略寬
    this.layoutWinBand(
      unityAnchorPortrait(0, CENTER_AY_P + CENTER_BG_DY, width, height),
      unityAnchorPortrait(0, CENTER_AY_P + WIN_LAYOUT_DY, width, height),
      s,
    )
    this.layoutMarqueeBand(
      unityAnchorPortrait(0, CENTER_AY_P + CENTER_BG_DY, width, height),
      s,
      MARQUEE_PLATE_W_P,
      MARQUEE_MASK_W_P,
    )

    // SPIN／免費遊戲大圓：正中央、距底緣 265。直版兩群 localScale 皆為 1（橫版 0.71），而共用 helper 的常數
    // 都已內含 ×0.71，故餵入「等效倍率」s / SPIN_SCALE。
    const spinCenter = at(0, SPIN_Y_P)
    this.layoutSpin(spinCenter, SPIN_BASE * s, s / SPIN_SCALE)
    this.layoutFreeGameDisc(spinCenter, s / SPIN_SCALE)
  }

  override destroy(options?: DestroyOptions): void {
    window.removeEventListener('keydown', this.keydownHandler)
    killTweensOf(this.winProxy)
    super.destroy(options)
  }
}

/** 置中定位（anchor 0.5 的元件與 IconButton 皆以中心為原點）。 */
function place(node: Container, at: { readonly x: number; readonly y: number }): void {
  node.position.set(at.x, at.y)
}

/** NineSliceSprite 無 anchor：由中心點換算左上角後定位並設定尺寸。 */
function placePlate(
  plate: NineSliceSprite,
  center: { readonly x: number; readonly y: number },
  w: number,
  h: number,
): void {
  plate.width = w
  plate.height = h
  plate.position.set(center.x - w / 2, center.y - h / 2)
}
